// @greenlight-spec: 10-security-compliance.md#default-policy-bundle
// @greenlight-spec: 28-it-dashboard-ui.md#policies--governance-policy-set
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuditRetentionSettingsSchema, PaginationQuerySchema, UpdateAuditRetentionSettingsSchema, paginated, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
export const PolicyEnforcementSchema = z.enum(['block', 'warn']);
/**
 * One shipped check with the org's tuning applied — the row the Settings →
 * Policies page renders. `is_active` is the raw toggle; `active` is whether the
 * check actually fires (off or awaiting config both make it inert, and
 * `inactive_reason` says which).
 */
export const PolicySettingSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    enforcement: PolicyEnforcementSchema,
    is_active: z.boolean(),
    active: z.boolean(),
    inactive_reason: z.enum(['disabled', 'awaiting_configuration']).optional(),
    requires_config: z.boolean(),
    /**
     * The bundle emits this check at `warn` severity only, so promoting it to
     * `block` would change nothing. The row renders without a Block option and
     * the service refuses one.
     */
    advisory: z.boolean(),
    config_shape: z.enum(['images', 'caps']).nullable(),
    config: z.record(z.unknown()).nullable(),
    /**
     * What the check falls back to with nothing configured, so the tuning form can
     * prefill the values actually in force rather than a literal of its own.
     */
    config_defaults: z.record(z.unknown()).nullable(),
    updated_at: z.string().nullable(),
});
export const PolicyUpdateBodySchema = z
    .object({
    is_active: z.boolean().optional(),
    enforcement: PolicyEnforcementSchema.optional(),
    config: z.record(z.unknown()).nullable().optional(),
})
    .refine((b) => b.is_active !== undefined || b.enforcement !== undefined || b.config !== undefined, { message: 'Provide at least one of is_active, enforcement, config.' });
/** One check row of an app's latest default-branch run, for the results view. */
export const PolicyResultCheckSchema = z.object({
    name: z.string(),
    status: z.enum(['running', 'pass', 'fail', 'skip']),
    error_summary: z.string().nullable(),
});
export const PolicyAppResultSchema = z.object({
    app_id: z.string().uuid(),
    app_name: z.string(),
    app_slug: z.string(),
    run_id: z.string().uuid(),
    commit_sha: z.string(),
    run_status: z.string(),
    completed_at: z.string().nullable(),
    checks: z.array(PolicyResultCheckSchema),
});
/**
 * A check an org requires from its own scanner (docs/07 §Required external
 * checks) — CodeQL, Snyk, anything posting a check run. Distinct from a shipped
 * check: Greenlight evaluates no rule here, it requires the result and relays it.
 *
 * `github_app_id` is not optional. A check matched on name alone is satisfiable
 * by anything able to write a status, which would advertise a gate a pull-request
 * author can turn green.
 */
export const ExternalCheckSchema = z.object({
    check_name: z.string(),
    github_app_id: z.number().int().positive(),
    enforcement: PolicyEnforcementSchema,
    is_active: z.boolean(),
    updated_at: z.string(),
    /** Pasted step list when Greenlight runs this scanner; null when it only requires an existing check. */
    steps_yaml: z.string().nullable(),
});
/**
 * Add or re-tune a check, keyed on `check_name`. The two posture fields are
 * optional and **omitting one keeps its stored value** — the defaults below
 * apply only when the check is first added, so re-sending the identity to
 * correct an app id cannot silently loosen or re-enable it.
 */
/**
 * What a fleet reconcile reached. Optional because the pass only runs where one is
 * wired; present means it ran, and `failed_repos` names what it could not reach —
 * a partial pass answering a bare 200 would be a lie IT could only find in the
 * audit log.
 */
export const ReconcileReportSchema = z.object({
    applied: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    failed_repos: z.array(z.string()),
});
export const ExternalCheckUpsertBodySchema = z
    .object({
    check_name: z.string().min(1).max(255),
    github_app_id: z.number().int().positive().optional(),
    /** YAML step list for Greenlight to run. Mutually exclusive with `github_app_id`. */
    steps_yaml: z.string().min(1).max(32_768).optional(),
    /** First added as `warn`: a newly named scanner reports before it can gate. */
    enforcement: PolicyEnforcementSchema.optional(),
    /** First added active. */
    is_active: z.boolean().optional(),
})
    .refine((b) => b.steps_yaml !== undefined || b.github_app_id !== undefined, {
    message: 'Provide github_app_id or steps_yaml.',
});
/**
 * The minimal policy tuning surface (docs/28 §Policies — the shipped slice):
 * list + update over the fixed catalog and the
 * read-only per-app results view. No rule authoring, no compiler, no catalog
 * packs — that is the deferred management system (docs/38).
 */
export const policiesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/policies',
        responses: {
            200: z.object({
                policies: z.array(PolicySettingSchema),
                recommended_base_images: z.array(z.string()),
            }),
            ...errorResponses,
        },
        summary: 'List the shipped deploy checks with this org’s tuning.',
    },
    update: {
        method: 'PATCH',
        path: '/api/policies/:policy_id',
        pathParams: z.object({ policy_id: z.string() }),
        body: PolicyUpdateBodySchema,
        responses: {
            200: z.object({ policy: PolicySettingSchema }),
            ...errorResponses,
        },
        summary: 'Tune one shipped check: on/off, block/warn, and its config values.',
    },
    results: {
        method: 'GET',
        path: '/api/policies/results',
        query: PaginationQuerySchema,
        responses: {
            200: paginated(PolicyAppResultSchema),
            ...errorResponses,
        },
        summary: 'Read-only: each app’s latest default-branch run and what its checks decided.',
    },
    listExternalChecks: {
        method: 'GET',
        path: '/api/policies/external-checks',
        responses: {
            200: z.object({ checks: z.array(ExternalCheckSchema) }),
            ...errorResponses,
        },
        summary: 'List the checks this org requires from its own scanners.',
    },
    upsertExternalCheck: {
        method: 'PUT',
        path: '/api/policies/external-checks',
        body: ExternalCheckUpsertBodySchema,
        responses: {
            200: z.object({ check: ExternalCheckSchema, reconcile: ReconcileReportSchema.optional() }),
            ...errorResponses,
        },
        summary: 'Add or re-tune an external check, keyed on its name.',
    },
    removeExternalCheck: {
        method: 'DELETE',
        // The name is a query parameter, not a path segment. Real check names carry
        // slashes, spaces and parens — `code/snyk (Shift)`, `CodeQL / Analyze` — and a
        // name a PUT can store must be a name a DELETE can address, or the row IT most
        // needs gone is the one they cannot remove.
        path: '/api/policies/external-checks',
        query: z.object({ check_name: z.string().min(1).max(255) }),
        // ts-rest types a bodyless DELETE as query-like, which would hand the client a
        // `useQuery` for a destructive call. An empty optional body keeps it a mutation.
        body: z.object({}).optional(),
        // 200 rather than 204: the reconcile report has to have somewhere to land.
        responses: {
            200: z.object({ reconcile: ReconcileReportSchema.optional() }),
            ...errorResponses,
        },
        summary: 'Stop requiring and reading an external check.',
    },
    getAuditRetention: {
        method: 'GET',
        path: '/api/policies/audit-retention',
        responses: {
            200: AuditRetentionSettingsSchema,
            ...errorResponses,
        },
        summary: 'How long this org keeps its audit log, in days; null keeps it indefinitely.',
    },
    updateAuditRetention: {
        method: 'PUT',
        path: '/api/policies/audit-retention',
        body: UpdateAuditRetentionSettingsSchema,
        responses: {
            200: AuditRetentionSettingsSchema,
            ...errorResponses,
        },
        summary: 'Set how long this org keeps its audit log (audited). A daily job deletes rows older than the window; null keeps the log indefinitely.',
    },
});
//# sourceMappingURL=policies.js.map